import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api/client";

const ADMIN_API_URL = import.meta.env.PROD
  ? "https://admin.stasht.app"
  : "http://localhost:3100";
const PERMISSION_CACHE_KEY = "stasht_impersonate_permission";
const PERMISSION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const ADMIN_BACKUP_KEY = "stasht_admin_backup";

interface PermissionCache {
  canImpersonate: boolean;
  timestamp: number;
}

const getCachedPermission = (): boolean | null => {
  try {
    const cached = localStorage.getItem(PERMISSION_CACHE_KEY);
    if (!cached) return null;
    const parsed: PermissionCache = JSON.parse(cached);
    if (Date.now() - parsed.timestamp > PERMISSION_CACHE_TTL) {
      localStorage.removeItem(PERMISSION_CACHE_KEY);
      return null;
    }
    return parsed.canImpersonate;
  } catch {
    return null;
  }
};

const setCachedPermission = (canImpersonate: boolean) => {
  const cache: PermissionCache = { canImpersonate, timestamp: Date.now() };
  localStorage.setItem(PERMISSION_CACHE_KEY, JSON.stringify(cache));
};

export const getAdminBackup = () => {
  try {
    const backup = localStorage.getItem(ADMIN_BACKUP_KEY);
    if (!backup) return null;
    const parsed = JSON.parse(backup);
    // Validate the backup has the shape written by saveAdminBackup —
    // prevents a regular user from faking impersonation with an empty object.
    if (
      typeof parsed?.accessToken === "string" &&
      typeof parsed?.refreshToken === "string" &&
      typeof parsed?.userData === "string" &&
      typeof parsed?.adminEmail === "string" &&
      parsed.accessToken.length > 0 &&
      parsed.adminEmail.length > 0
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

export function useCanImpersonate() {
  const [canImpersonate, setCanImpersonate] = useState<boolean | null>(null);
  const isImpersonating = !!getAdminBackup();

  useEffect(() => {
    // If already impersonating, we know they have permission
    if (isImpersonating) {
      setCanImpersonate(true);
      return;
    }

    // Check cache first
    const cached = getCachedPermission();
    if (cached !== null) {
      setCanImpersonate(cached);
      return;
    }

    // Fetch from API
    const checkPermission = async () => {
      try {
        const authHeaders = await apiClient.getAuthHeaders();
        const response = await fetch(`${ADMIN_API_URL}/api/users/impersonate/check`, {
          headers: authHeaders,
        });

        if (response.ok) {
          const data = await response.json();
          setCanImpersonate(data.canImpersonate);
          setCachedPermission(data.canImpersonate);
        } else {
          setCanImpersonate(false);
          setCachedPermission(false);
        }
      } catch {
        setCanImpersonate(false);
      }
    };

    checkPermission();
  }, [isImpersonating]);

  return { canImpersonate: canImpersonate || isImpersonating, isImpersonating };
}
